import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Instagram, Facebook, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { campaigns } from '../data/mockData';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCampaignsDropdownOpen, setIsCampaignsDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;
  const isCampaignsActive = location.pathname.includes('/campaigns');

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-3' : 'bg-white py-4'
      }`}
    >
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
            <img
              src="/assets/MG Logo.png"
              alt="Hon Maryka Groenewald MLC"
              className="h-16 md:h-20"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-medium tracking-wide transition-colors hover:text-gold ${
                isActive('/') ? 'text-gold' : 'text-gray-700'
              }`}
            >
              HOME
            </Link>
            <Link
              to="/about"
              className={`text-sm font-medium tracking-wide transition-colors hover:text-gold ${
                isActive('/about') ? 'text-gold' : 'text-gray-700'
              }`}
            >
              ABOUT
            </Link>
            
            {/* Campaigns Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsCampaignsDropdownOpen(true)}
              onMouseLeave={() => setIsCampaignsDropdownOpen(false)}
            >
              <button
                className={`text-sm font-medium tracking-wide transition-colors hover:text-gold flex items-center ${
                  isCampaignsActive ? 'text-gold' : 'text-gray-700'
                }`}
              >
                CAMPAIGNS
                <ChevronDown size={16} className="ml-1" />
              </button>
              
              {isCampaignsDropdownOpen && (
                <div className="absolute top-full left-0 pt-2 z-50">
                  <div className="w-64 bg-white shadow-lg rounded-md py-2">
                    {campaigns.filter((campaign) => !campaign.hidden).slice(0, 4).map((campaign) => (
                      <Link
                        key={campaign.id}
                        to={`/campaigns/${campaign.id}`}
                        className="block px-4 py-3 text-sm text-gray-700 hover:bg-cream hover:text-gold transition-colors"
                      >
                        {campaign.title}
                      </Link>
                    ))}
                    <Link
                      to="/campaigns"
                      className="block px-4 py-3 text-sm font-bold text-gold hover:bg-cream transition-colors border-t border-gray-200 mt-2"
                    >
                      VIEW ALL CAMPAIGNS →
                    </Link>
                  </div>
                </div>
              )}
            </div>
            
            <Link
              to="/policies"
              className={`text-sm font-medium tracking-wide transition-colors hover:text-gold ${
                isActive('/policies') ? 'text-gold' : 'text-gray-700'
              }`}
            >
              POLICIES
            </Link>
            <Link
              to="/media"
              className={`text-sm font-medium tracking-wide transition-colors hover:text-gold ${
                isActive('/media') ? 'text-gold' : 'text-gray-700'
              }`}
            >
              MEDIA
            </Link>
            <Link
              to="/contact"
              className={`text-sm font-medium tracking-wide transition-colors hover:text-gold ${
                isActive('/contact') ? 'text-gold' : 'text-gray-700'
              }`}
            >
              CONTACT
            </Link>

            {/* Social Icons */}
            <div className="flex items-center space-x-3 ml-4 pl-4 border-l border-gray-300">
              <a
                href="https://www.instagram.com/maryka.groenewald.mlcforwa/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-700 hover:text-gold transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://www.facebook.com/MarykaGroenewaldAC"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-700 hover:text-gold transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
            </div>

            {/* CTA Button */}
            <Button
              asChild
              className="bg-gold hover:bg-[#e09f0a] text-white font-medium px-6 py-2 rounded-md transition-colors"
            >
              <Link to="/contact">Get Involved</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-navy"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M4 6h16M4 12h16M4 18h16"></path>
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4">
            <div className="flex flex-col space-y-3">
              <Link
                to="/"
                className={`text-sm font-medium tracking-wide transition-colors hover:text-gold ${
                  isActive('/') ? 'text-gold' : 'text-gray-700'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                HOME
              </Link>
              <Link
                to="/about"
                className={`text-sm font-medium tracking-wide transition-colors hover:text-gold ${
                  isActive('/about') ? 'text-gold' : 'text-gray-700'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                ABOUT
              </Link>
              <Link
                to="/campaigns"
                className={`text-sm font-medium tracking-wide transition-colors hover:text-gold ${
                  isCampaignsActive ? 'text-gold' : 'text-gray-700'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                CAMPAIGNS
              </Link>
              <Link
                to="/policies"
                className={`text-sm font-medium tracking-wide transition-colors hover:text-gold ${
                  isActive('/policies') ? 'text-gold' : 'text-gray-700'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                POLICIES
              </Link>
              <Link
                to="/media"
                className={`text-sm font-medium tracking-wide transition-colors hover:text-gold ${
                  isActive('/media') ? 'text-gold' : 'text-gray-700'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                MEDIA
              </Link>
              <Link
                to="/contact"
                className={`text-sm font-medium tracking-wide transition-colors hover:text-gold ${
                  isActive('/contact') ? 'text-gold' : 'text-gray-700'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                CONTACT
              </Link>
              <div className="flex items-center space-x-4 pt-2">
                <a
                  href="https://www.instagram.com/maryka.groenewald.mlcforwa/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-700 hover:text-gold transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram size={20} />
                </a>
                <a
                  href="https://www.facebook.com/MarykaGroenewaldAC"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-700 hover:text-gold transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook size={20} />
                </a>
              </div>
              <Button
                asChild
                className="bg-gold hover:bg-[#e09f0a] text-white font-medium px-6 py-2 rounded-md transition-colors w-full"
              >
                <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                  Get Involved
                </Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
